/*
 * Kalmix custom code
 * File: assets/kmx-home-apply.js
 * Version: 1009.1
 * History:
 * - 1009.1 The drawing board's status chip reads DEMO instead of LIVE (OPEN on the
 *   Build with Kalmix slot is unchanged), so the workflow animation is not read as
 *   live data.
 * - 1008.4 Auto-advance resumes when the visitor leaves the act and comes back after
 *   taking over. Reports the current path's progress to the right-hand pager
 *   (KMX.progress). The readout's second line is green only when it states RTK
 *   FIXED, neutral otherwise. Ice-blue track unified with 04 / 06 (1.8 px, 2.4 px
 *   near the head).
 * - 1008.3 Point cloud reads as building outlines: points gather on corner edges,
 *   roof edges, footprints and floor lines, walls keep only sparse points; the
 *   round tree clusters are replaced by a gabled house, a kiosk, a warehouse and
 *   a wall. Street poles unchanged.
 * - 1008.2 Your application becomes an oblique 3D site: a perspective ground grid,
 *   a generated point cloud (ground, blocks, poles, trees) revealed by a scan ring
 *   and sweep around the head; freshly scanned points jitter as a double image and
 *   then settle into place as the RTK path registers them. The dashed line runs on
 *   the ground to "your next point" (placed with the cursor, lifted label), and a
 *   click on the board opens the card's Build with Kalmix link (touch: tap the
 *   point). Readout reads OPEN · BUILD WITH KALMIX. Flat grid, rulers and scale
 *   bar are skipped in this view.
 * - 1008.1 Initial delivery of 05 APPLY. Accordion path cards (auto-advance with
 *   progress, hover holds, click takes over) and a live drawing board on canvas:
 *   grid, rulers with cursor marks, registration marks, north arrow, scale bar,
 *   title block. On every switch ~140 position fixes flow from the previous path
 *   into the next, the scene's linework draws in and labels decode; a glowing
 *   head then runs the path with a fading tail, lighting the fixes it passes.
 *   Field & survey: boundary traverse (points stored) then AB passes with the
 *   covered swath. Robotics: lanes bend around a keep-out ring, mowed stripes,
 *   return to dock. Vehicle testing: three laps, earlier laps stay as ghosts,
 *   lap table. Your application: the fixed line ends in a dashed line that
 *   follows the cursor to "your next point". Hovering the board snaps a
 *   crosshair to the nearest driven point. Drawing is authored on a 625 x 588
 *   grid and scaled to fit. Runs only while the act is visible; reduced motion
 *   shows each scene's finished drawing.
 */
(function () {
  'use strict';

  var LW = 625, LH = 588, ICE = '95,182,236', ENV = 'rgba(143,163,171,.30)', STR = 'rgba(191,227,255,.55)';
  var MORPH = 1.1, N = 140, SCR = '#%&/<>0123456789ABCDEF';

  /* ======================= 几何工具 ======================= */
  function V(x, y) { return { x: x, y: y }; }
  function cl(x, a, b) { a = a == null ? 0 : a; b = b == null ? 1 : b; return Math.max(a, Math.min(b, x)); }
  function ease(t) { return t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }
  function rndf(seed) { return function () { seed |= 0; seed = seed + 0x6D2B79F5 | 0; var t = Math.imul(seed ^ seed >>> 15, 1 | seed); t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t; return ((t ^ t >>> 14) >>> 0) / 4294967296; }; }
  function sLine(a, b, out, tag) { var L = Math.hypot(b.x - a.x, b.y - a.y), n = Math.max(1, Math.ceil(L / 3)); for (var i = 1; i <= n; i++) { var t = i / n; out.push({ x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, tag: tag }); } }
  function sQuad(a, c, b, out, tag) { var L = Math.hypot(c.x - a.x, c.y - a.y) + Math.hypot(b.x - c.x, b.y - c.y), n = Math.max(2, Math.ceil(L / 3)); for (var i = 1; i <= n; i++) { var t = i / n, u = 1 - t; out.push({ x: u * u * a.x + 2 * u * t * c.x + t * t * b.x, y: u * u * a.y + 2 * u * t * c.y + t * t * b.y, tag: tag }); } }
  function roundPoly(P, r, out, tag) {
    if (!out.length) out.push({ x: P[0].x, y: P[0].y, tag: tag });
    for (var i = 1; i < P.length; i++) {
      var p = P[i];
      if (i === P.length - 1) { sLine(out[out.length - 1], p, out, tag); break; }
      var a = P[i - 1], b = P[i + 1], la = Math.hypot(p.x - a.x, p.y - a.y), lb = Math.hypot(b.x - p.x, b.y - p.y), rr = Math.min(r, la / 2, lb / 2);
      var p1 = V(p.x - (p.x - a.x) / la * rr, p.y - (p.y - a.y) / la * rr), p2 = V(p.x + (b.x - p.x) / lb * rr, p.y + (b.y - p.y) / lb * rr);
      sLine(out[out.length - 1], p1, out, tag); sQuad(p1, p, p2, out, tag);
    }
    return out;
  }
  function catmull(P, closed) {
    var out = [], n = P.length, segs = closed ? n : n - 1;
    function g(i) { return P[closed ? (i + n) % n : Math.max(0, Math.min(n - 1, i))]; }
    out.push({ x: P[0].x, y: P[0].y });
    for (var i = 0; i < segs; i++) {
      var p0 = g(i - 1), p1 = g(i), p2 = g(i + 1), p3 = g(i + 2), m = Math.max(2, Math.ceil(Math.hypot(p2.x - p1.x, p2.y - p1.y) / 3));
      for (var k = 1; k <= m; k++) {
        var t = k / m, t2 = t * t, t3 = t2 * t;
        out.push({
          x: .5 * ((2 * p1.x) + (-p0.x + p2.x) * t + (2 * p0.x - 5 * p1.x + 4 * p2.x - p3.x) * t2 + (-p0.x + 3 * p1.x - 3 * p2.x + p3.x) * t3),
          y: .5 * ((2 * p1.y) + (-p0.y + p2.y) * t + (2 * p0.y - 5 * p1.y + 4 * p2.y - p3.y) * t2 + (-p0.y + 3 * p1.y - 3 * p2.y + p3.y) * t3)
        });
      }
    }
    return out;
  }
  function measure(P) { var d = 0; P[0].d = 0; for (var i = 1; i < P.length; i++) { d += Math.hypot(P[i].x - P[i - 1].x, P[i].y - P[i - 1].y); P[i].d = d; } P.L = d; return P; }
  function idxAt(P, d) { var lo = 0, hi = P.length - 1; while (lo < hi) { var m = (lo + hi) >> 1; if (P[m].d < d) lo = m + 1; else hi = m; } return lo; }
  function at(P, d) { d = Math.max(0, Math.min(P.L, d)); var i = Math.max(1, idxAt(P, d)), a = P[i - 1], b = P[i], t = (d - a.d) / ((b.d - a.d) || 1); return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t, ang: Math.atan2(b.y - a.y, b.x - a.x), i: i }; }
  function resample(P, n) { var o = []; for (var k = 0; k < n; k++) { var d = P.L * k / (n - 1), p = at(P, d); o.push({ x: p.x, y: p.y, d: d }); } return o; }
  function smooth(P, w) { var o = []; for (var i = 0; i < P.length; i++) { var sx = 0, sy = 0, c = 0; for (var k = -w; k <= w; k++) { var q = P[Math.max(0, Math.min(P.length - 1, i + k))]; sx += q.x; sy += q.y; c++; } o.push({ x: sx / c, y: sy / c, tag: P[i].tag }); } return o; }
  function pad2(n) { return (n < 10 ? '0' : '') + n; }
  function tagBack(P, i) { for (var j = i; j >= 0; j--) if (P[j].tag >= 0) return P[j].tag; return 0; }

  /* ======================= 场景几何（逻辑坐标 625 x 588） ======================= */
  function buildField() {
    var th = -3.5 * Math.PI / 180, O = V(132, 112);
    function X(u, v) { return V(O.x + u * Math.cos(th) - v * Math.sin(th), O.y + u * Math.sin(th) + v * Math.cos(th)); }
    var B = [X(-6, 4), X(398, -8), X(404, 346), X(-2, 352)];
    var P = []; roundPoly([B[0], B[1], B[2], B[3], B[0], X(14, 26), X(30, 50)], 10, P, -1);
    var dir = 1;
    for (var k = 0; k < 7; k++) {
      var v = 50 + 40 * k, u1 = dir > 0 ? 360 : 30;
      sLine(P[P.length - 1], X(u1, v), P, k);
      if (k < 6) for (var j = 1; j <= 24; j++) { var a = -Math.PI / 2 + Math.PI * j / 24; var q = X(u1 + dir * 20 * Math.cos(a), v + 20 + 20 * Math.sin(a)); P.push({ x: q.x, y: q.y, tag: -2 }); }
      dir = -dir;
    }
    measure(P);
    var ev = B.map(function (b, i) {
      if (!i) return { d: 0, p: b, label: 'P-101' };
      var best = 0, bd = 1e9; for (var j = 0; j < P.length * .45; j++) { var dd = Math.hypot(P[j].x - b.x, P[j].y - b.y); if (dd < bd && P[j].d > 40) { bd = dd; best = j; } }
      return { d: P[best].d, p: b, label: 'P-10' + (i + 1) };
    });
    var cont = [];
    [[V(60, 540), [40, 70, 100, 130, 160, 190]], [V(590, 60), [30, 58, 86, 114]], [V(330, 300), [220, 250]]].forEach(function (h, hi) {
      h[1].forEach(function (R, j) { var pts = []; for (var a = 0; a <= 64; a++) { var t = a / 64 * Math.PI * 2, r = R + R * .08 * Math.sin(3 * t + hi + j * .4) + R * .05 * Math.sin(5 * t + j); pts.push(V(h[0].x + r * Math.cos(t), h[0].y + r * Math.sin(t))); } cont.push(pts); });
    });
    return {
      kind: 'field', P: P, ev: ev, B: B, X: X, cont: cont, swath: 38,
      head: [X(30, 30), X(360, 30), X(360, 310), X(30, 310), X(30, 30)],
      cp: [[X(-46, 396), 'CP1'], [X(440, -46), 'CP2']],
      legend: '<span><i></i>driven line</span><span><i class="s"></i>covered swath</span><span><i class="e"></i>surveyed boundary</span>',
      state: function (d) {
        if (d < ev[3].d + 20) { var k = 0; ev.forEach(function (e, i) { if (d >= e.d) k = i; }); return 'SURVEY · ' + ev[k].label + ' STORED'; }
        return 'PASS ' + pad2(tagBack(P, at(P, d).i) + 1) + '/07 · RTK FIXED';
      }
    };
  }

  function buildRobot() {
    var tree = V(330, 320), Rk = 52, pts = [], dir = 1, lanes = [];
    for (var k = 0; k < 10; k++) { var y = 100 + 40 * k; lanes.push({ y: y, x0: 92, x1: y < 160 ? 430 : 536 }); }
    lanes.forEach(function (ln, k) {
      var a = dir > 0 ? ln.x0 : ln.x1, b = dir > 0 ? ln.x1 : ln.x0, seg = [], n = Math.ceil(Math.abs(b - a) / 3), R = Rk + 16;
      for (var j = 0; j <= n; j++) {
        var x = a + (b - a) * j / n, y = ln.y, dx = x - tree.x;
        if (Math.abs(dx) < R) { var s = Math.sqrt(Math.max(0, R * R - dx * dx)); y = ln.y < tree.y ? Math.min(y, tree.y - s) : Math.max(y, tree.y + s); }
        seg.push({ x: x, y: y, tag: k });
      }
      smooth(seg, 9).forEach(function (p) { pts.push(p); });
      if (k < lanes.length - 1) for (var i = 1; i < 24; i++) { var t = -Math.PI / 2 + Math.PI * i / 24; pts.push({ x: b + dir * 20 * Math.cos(t), y: ln.y + 20 + 20 * Math.sin(t), tag: -2 }); }
      dir = -dir;
    });
    var last = pts[pts.length - 1]; sQuad(last, V(last.x - 18, last.y + 6), V(91, 498), pts, -3);
    measure(pts);
    return {
      kind: 'robot', P: pts, tree: tree, Rk: Rk, swath: 36,
      legend: '<span><i></i>driven route</span><span><i class="s"></i>mowed lanes</span><span><i class="d"></i>keep-out</span>',
      state: function (d) {
        if (d >= pts.L - 2) return 'DOCKED · RTK FIXED';
        var i = at(pts, d).i; if (pts[i].tag === -3) return 'RETURN TO DOCK · RTK FIXED';
        return 'LANE ' + pad2(tagBack(pts, i) + 1) + '/10 · RTK FIXED';
      }
    };
  }

  function buildTrack() {
    var C = [V(108, 300), V(126, 160), V(222, 98), V(372, 118), V(492, 92), V(548, 170), V(504, 250), V(404, 268), V(344, 326), V(392, 404), V(508, 414), V(544, 478), V(470, 526), V(252, 520), V(142, 468)];
    var base = measure(catmull(C, true));
    function nrm(i) { var a = base[Math.max(0, i - 1)], b = base[Math.min(base.length - 1, i + 1)], L = Math.hypot(b.x - a.x, b.y - a.y) || 1; return V(-(b.y - a.y) / L, (b.x - a.x) / L); }
    var laps = [0, 1, 2].map(function (k) {
      var r = rndf(7 + k), ph1 = r() * 6, ph2 = r() * 6;
      return measure(base.map(function (p, i) { var n = nrm(i), off = 5 * Math.sin(p.d * .011 + ph1) + 3 * Math.sin(p.d * .027 + ph2); return { x: p.x + n.x * off, y: p.y + n.y * off }; }));
    });
    var P = []; laps.forEach(function (l, k) { l.forEach(function (p, i) { if (k && !i) return; P.push({ x: p.x, y: p.y, tag: k }); }); }); measure(P);
    var L = [], R = []; for (var i = 0; i < base.length; i += 2) { var m = nrm(i); L.push(V(base[i].x + m.x * 15, base[i].y + m.y * 15)); R.push(V(base[i].x - m.x * 15, base[i].y - m.y * 15)); }
    return {
      kind: 'track', P: P, base: base, laps: laps, nrm: nrm, edgeL: L, edgeR: R, lapT: ['1:43.2', '1:42.6', '1:41.9'], lapD: ['', '−0.6', '−0.7'],
      legend: '<span><i></i>current lap</span><span><i class="o"></i>earlier laps</span><span><i class="e"></i>track edge</span>',
      lap: function (d) { return Math.min(2, Math.floor(d / (P.L / 3))); },
      state: function (d) { return 'LAP ' + pad2(this.lap(d) + 1) + '/03 · RTK FIXED'; }
    };
  }

  /* 04 · Your application：斜俯视 3D 场地 + 点云（世界坐标，单位约 1 m） */
  var CAM = { p: 50 * Math.PI / 180, dist: 95, f: 740, cx: 312, cy: 262 };
  CAM.cp = Math.cos(CAM.p); CAM.sp = Math.sin(CAM.p);
  function proj(x, y, z, yaw) {
    var cs = Math.cos(yaw), sn = Math.sin(yaw), x1 = x * cs - y * sn, y1 = x * sn + y * cs;
    var d = y1 * CAM.cp - z * CAM.sp + CAM.dist, v = y1 * CAM.sp + z * CAM.cp;
    return { x: CAM.cx + CAM.f * x1 / d, y: CAM.cy - CAM.f * v / d, k: CAM.f / d, d: d };
  }
  function unproj(sx, sy, yaw) { // 屏幕点 → 地面（z = 0）
    var a = (sx - CAM.cx) / CAM.f, b = (CAM.cy - sy) / CAM.f, den = CAM.sp - b * CAM.cp;
    if (den < .05) den = .05;
    var y1 = b * CAM.dist / den, x1 = a * (y1 * CAM.cp + CAM.dist), cs = Math.cos(yaw), sn = Math.sin(yaw);
    return V(cl(x1 * cs + y1 * sn, -28, 28), cl(-x1 * sn + y1 * cs, -28, 28));
  }
  function buildApp() {
    var r = rndf(11), pts = [], i, j;
    function hgt(x, y) { return .5 * Math.sin(x * .13) + .4 * Math.cos(y * .17) + .25 * Math.sin((x + y) * .3); }
    for (i = 0; i < 2600; i++) { var gx = -30 + r() * 60, gy = -30 + r() * 60; pts.push({ x: gx, y: gy, z: hgt(gx, gy), k: 0 }); }
    // 建筑：点集中在棱线与楼层线上（轮廓感），墙面只留稀疏点
    function edge(a, b, step, out) { var L = Math.hypot(b[0] - a[0], b[1] - a[1], b[2] - a[2]), n = Math.max(2, Math.ceil(L / step)); for (var m = 0; m <= n; m++) { var u = m / n; out.push({ x: a[0] + (b[0] - a[0]) * u, y: a[1] + (b[1] - a[1]) * u, z: a[2] + (b[2] - a[2]) * u, k: 1, e: 1 }); } }
    function box(x, y, w, d, h) {
      var C = [[x, y], [x + w, y], [x + w, y + d], [x, y + d]], m, zz;
      for (m = 0; m < 4; m++) {
        var A = C[m], B = C[(m + 1) % 4];
        edge([A[0], A[1], 0], [A[0], A[1], h], .2, pts);           // 竖棱
        edge([A[0], A[1], h], [B[0], B[1], h], .2, pts);           // 屋顶边
        edge([A[0], A[1], 0], [B[0], B[1], 0], .35, pts);          // 落地线
        for (zz = 3; zz < h - .5; zz += 3) edge([A[0], A[1], zz], [B[0], B[1], zz], .45, pts); // 楼层线
      }
      var nf = Math.round((w + d) * Math.min(h, 5) * .45);
      for (m = 0; m < nf; m++) { var sd = r() * 4 | 0, u = r(), a2 = C[sd], b2 = C[(sd + 1) % 4]; pts.push({ x: a2[0] + (b2[0] - a2[0]) * u, y: a2[1] + (b2[1] - a2[1]) * u, z: r() * h, k: 1 }); }
      for (m = 0; m < w * d * .25; m++) pts.push({ x: x + r() * w, y: y + r() * d, z: h, k: 1 });
    }
    function gable(x, y, w, d, h, rh) { // 坡屋顶小屋：屋脊沿 x 方向
      box(x, y, w, d, h);
      var my = y + d / 2;
      edge([x, my, h + rh], [x + w, my, h + rh], .2, pts);
      [[x, y], [x, y + d], [x + w, y], [x + w, y + d]].forEach(function (c0) { edge([c0[0], c0[1], h], [c0[0], my, h + rh], .2, pts); });
    }
    box(-21, 6, 8, 6, 7); box(12, -17, 7, 9, 5); box(17, 13, 9, 7, 10); box(-5, 19, 6, 5, 4);
    gable(-27, -10, 6, 5, 3, 2); box(2, 13, 3, 2, 2.6); box(21, -25, 8, 6, 5); box(-21, -27, 10, .5, 2);
    [[-8, -20], [1, -9], [8, -1], [-15, 1], [22, -3]].forEach(function (q) {
      for (j = 0; j < 26; j++) pts.push({ x: q[0], y: q[1], z: j * .22, k: 2 });
      for (j = 0; j < 14; j++) pts.push({ x: q[0] + (r() - .5), y: q[1] + (r() - .5), z: 5.6 + r() * .5, k: 2 });
    });
    var W = measure(catmull([V(-24, -23), V(-17, -15), V(-11, -6), V(-4, 0), V(4, 4), V(9, 10)], false));
    W.forEach(function (q) { q.tag = 0; });
    pts.forEach(function (q) {
      q.jx = (r() - .5) * 2.6; q.jy = (r() - .5) * 2.6; q.jz = (r() - .5) * 1.4; q.rd = -1;
      for (var m = 0; m < W.length; m += 3) if (Math.hypot(W[m].x - q.x, W[m].y - q.y) < 16) { q.rd = W[m].d; break; }
    });
    return {
      kind: 'app', P: W, cloud: pts, solid: 4.6, wdots: resample(W, N), yaw: 0,
      legend: '<span><i></i>fixed path</span><span><i class="d"></i>yours to draw</span><span><i class="p"></i>point cloud</span><span class="r">Hardware + engineering support</span>',
      state: function () { return 'OPEN · BUILD WITH KALMIX'; }
    };
  }
  var BUILD = { field: buildField, robot: buildRobot, track: buildTrack, app: buildApp };

  /* ======================= 初始化 ======================= */
  function initApply(sec) {
    if (sec.__kmxInit) return; sec.__kmxInit = true;
    var KMX = window.KMX || { reduce: false, onEnter: function () {}, onVisible: function () {} };
    var reduce = KMX.reduce;
    var q = function (k) { return sec.querySelector('[data-k="' + k + '"]'); };
    var cards = Array.prototype.slice.call(sec.querySelectorAll('.kmx-app__card'));
    var cv = sec.querySelector('.kmx-app__cv'), board = sec.querySelector('.kmx-app__board'), stage = sec.querySelector('.kmx-app__stage');
    var kdots = Array.prototype.slice.call(sec.querySelectorAll('.kmx-app__kd'));
    var dwell = parseFloat(sec.getAttribute('data-dwell')) || 11;
    if (!cards.length || !cv) return;
    var c = cv.getContext('2d');

    var G = cards.map(function (el) { var g = (BUILD[el.getAttribute('data-kind')] || buildApp)(); g.title = (el.querySelector('.kmx-app__t') || {}).textContent || ''; return g; });
    G.forEach(function (g) {
      g.dots = g.wdots ? g.wdots.map(function (w) { var o = proj(w.x, w.y, 0, .06); return { x: o.x, y: o.y, d: w.d }; }) : resample(g.base || g.P, N);
      g.D = g.kind === 'app' ? Math.max(7, dwell - 2) : dwell;
    });
    // 首次进入：定位点从随机散布处汇聚
    var r0 = rndf(3), scatter = { dots: [] }; for (var i = 0; i < N; i++) scatter.dots.push({ x: 40 + r0() * (LW - 80), y: 40 + r0() * (LH - 80) });

    var cur = 0, prev = scatter, t = 0, auto = !reduce, hold = false, visible = false, started = false, raf = 0, last = 0;
    var cw = 0, ch = 0, s = 1, ox = 0, oy = 0, DPR = 1, mouse = null, tgt = V(470, 170);
    var fine = window.matchMedia('(pointer: fine)').matches;

    /* ---------- 画布尺寸 ---------- */
    function size() {
      var r = cv.getBoundingClientRect(); cw = Math.max(1, r.width); ch = Math.max(1, r.height);
      DPR = Math.min(2, window.devicePixelRatio || 1);
      cv.width = Math.round(cw * DPR); cv.height = Math.round(ch * DPR);
      s = Math.min(cw / LW, ch / LH); ox = (cw - LW * s) / 2; oy = (ch - LH * s) / 2;
      placeDots(); if (!raf) draw();
    }

    /* ---------- 绘图原语（逻辑坐标） ---------- */
    function decode(str, qv, seed) {
      var n = Math.floor(qv * str.length); if (n >= str.length) return str;
      var o = str.slice(0, n), r = rndf(seed + Math.floor(t * 16));
      for (var i = n; i < str.length; i++) o += str[i] === ' ' ? ' ' : SCR[Math.floor(r() * SCR.length)];
      return o;
    }
    function txt(str, x, y, o) {
      o = o || {}; c.font = (o.w || 400) + ' ' + (o.fs || 9.5) + 'px "JetBrains Mono", ui-monospace, monospace';
      c.fillStyle = o.c || 'rgba(143,163,171,.85)'; c.textAlign = o.a || 'left'; c.textBaseline = 'alphabetic';
      if (o.q != null) str = decode(str, o.q, o.seed || 1);
      if ('letterSpacing' in c) c.letterSpacing = (o.ls == null ? 1.2 : o.ls) + 'px';
      c.fillText(str, x, y);
    }
    function poly(P, p, o) {
      if (p <= 0) return; o = o || {}; var n = Math.max(2, Math.floor(P.length * p));
      c.beginPath(); c.moveTo(P[0].x, P[0].y); for (var i = 1; i < n; i++) c.lineTo(P[i].x, P[i].y); if (o.close && p >= 1) c.closePath();
      c.strokeStyle = o.c || ENV; c.lineWidth = o.w || 1; c.setLineDash(o.dash || []); c.globalAlpha = o.a == null ? 1 : o.a; c.lineCap = 'round'; c.lineJoin = 'round'; c.stroke(); c.setLineDash([]); c.globalAlpha = 1;
    }
    function seg(P, i0, i1, o) {
      if (i1 - i0 < 1) return; c.beginPath(); c.moveTo(P[i0].x, P[i0].y); for (var i = i0 + 1; i <= i1; i++) c.lineTo(P[i].x, P[i].y);
      c.strokeStyle = o.c; c.lineWidth = o.w; c.globalAlpha = o.a == null ? 1 : o.a; c.lineCap = o.cap || 'round'; c.lineJoin = 'round'; c.stroke(); c.globalAlpha = 1;
    }
    function pt(x, y, r, o) { o = o || {}; c.beginPath(); c.arc(x, y, r, 0, Math.PI * 2); if (o.f) { c.fillStyle = o.f; c.fill(); } if (o.s) { c.strokeStyle = o.s; c.lineWidth = o.w || 1.3; c.stroke(); } }
    function rrect(x, y, w, h, r) { c.beginPath(); if (c.roundRect) c.roundRect(x, y, w, h, r); else c.rect(x, y, w, h); }

    /* ---------- 图纸框（画布像素坐标） ---------- */
    function chrome() {
      var x, y, k, step = 25 * s, flat = G[cur].kind !== 'app';
      c.lineWidth = 1;
      if (flat) {
      c.strokeStyle = 'rgba(124,200,255,.045)'; c.beginPath();
      for (x = ox + step; x < cw; x += step) { c.moveTo(Math.round(x) + .5, 18); c.lineTo(Math.round(x) + .5, ch); }
      for (y = oy + step; y < ch; y += step) { c.moveTo(18, Math.round(y) + .5); c.lineTo(cw, Math.round(y) + .5); }
      c.stroke();
      c.strokeStyle = 'rgba(124,200,255,.075)'; c.beginPath();
      for (x = ox + step * 4; x < cw; x += step * 4) { c.moveTo(Math.round(x) + .5, 18); c.lineTo(Math.round(x) + .5, ch); }
      for (y = oy + step * 4; y < ch; y += step * 4) { c.moveTo(18, Math.round(y) + .5); c.lineTo(cw, Math.round(y) + .5); }
      c.stroke();
      // 刻度尺（10 逻辑单位 = 1 m）
      c.fillStyle = 'rgba(7,18,26,.85)'; c.fillRect(0, 0, cw, 18); c.fillRect(0, 0, 18, ch);
      c.strokeStyle = 'rgba(143,163,171,.35)'; c.beginPath();
      var tick = 10 * s;
      for (k = 1; ox + k * tick < cw; k++) { x = Math.round(ox + k * tick) + .5; if (x < 20) continue; c.moveTo(x, 18); c.lineTo(x, 18 - (k % 5 ? 3 : 7)); }
      for (k = 1; oy + k * tick < ch; k++) { y = Math.round(oy + k * tick) + .5; if (y < 20) continue; c.moveTo(18, y); c.lineTo(18 - (k % 5 ? 3 : 7), y); }
      c.moveTo(0, 18.5); c.lineTo(cw, 18.5); c.moveTo(18.5, 0); c.lineTo(18.5, ch); c.stroke();
      for (k = 10; ox + k * tick < cw - 20; k += 10) txt(k + 'm', ox + k * tick + 3, 9, { fs: 7.5, c: 'rgba(143,163,171,.55)', ls: .8 });
      if (mouse && G[cur].kind !== 'app') {
        var mx = ox + mouse.x * s, my = oy + mouse.y * s; c.fillStyle = 'rgb(' + ICE + ')';
        c.beginPath(); c.moveTo(mx, 18); c.lineTo(mx - 3.5, 12); c.lineTo(mx + 3.5, 12); c.fill();
        c.beginPath(); c.moveTo(18, my); c.lineTo(12, my - 3.5); c.lineTo(12, my + 3.5); c.fill();
      }
      }
      // 对位标记
      [[30, 30], [cw - 12, 30], [30, ch - 12], [cw - 12, ch - 12]].forEach(function (m) { c.strokeStyle = 'rgba(191,227,255,.35)'; c.beginPath(); c.moveTo(m[0] - 6, m[1]); c.lineTo(m[0] + 6, m[1]); c.moveTo(m[0], m[1] - 6); c.lineTo(m[0], m[1] + 6); c.stroke(); });
      // 指北针
      var nx = cw - 40, ny = 58; c.strokeStyle = 'rgba(191,227,255,.5)'; c.beginPath(); c.moveTo(nx, ny + 16); c.lineTo(nx, ny - 12); c.stroke();
      c.fillStyle = 'rgba(191,227,255,.6)'; c.beginPath(); c.moveTo(nx, ny - 16); c.lineTo(nx - 4, ny - 6); c.lineTo(nx + 4, ny - 6); c.fill();
      txt('N', nx, ny + 28, { a: 'center', fs: 8.5, c: 'rgba(191,227,255,.6)' });
      if (cw < 480) return; // 窄屏只保留网格、刻度尺、对位标记和指北针
      // 比例尺（10 m）
      var sx = 40, sy = ch - 26, sw = 100 * s;
      if (flat) {
      for (k = 0; k < 4; k++) { c.fillStyle = k % 2 ? 'rgba(191,227,255,.08)' : 'rgba(191,227,255,.45)'; c.fillRect(sx + k * sw / 4, sy, sw / 4, 4); }
      c.strokeStyle = 'rgba(191,227,255,.4)'; c.strokeRect(sx + .5, sy + .5, sw, 4);
      txt('0', sx, sy - 5, { fs: 7.5 }); txt('5', sx + sw / 2, sy - 5, { fs: 7.5, a: 'center' }); txt('10 m', sx + sw, sy - 5, { fs: 7.5, a: 'center' });
      }
      // 图签
      var tx = cw - 198, ty = ch - 74; c.strokeStyle = 'rgba(191,227,255,.22)'; c.fillStyle = 'rgba(8,17,24,.7)'; c.fillRect(tx, ty, 182, 58); c.strokeRect(tx + .5, ty + .5, 182, 58);
      c.beginPath(); c.moveTo(tx, ty + 20.5); c.lineTo(tx + 182, ty + 20.5); c.moveTo(tx + 91.5, ty + 20); c.lineTo(tx + 91.5, ty + 58); c.moveTo(tx, ty + 39.5); c.lineTo(tx + 182, ty + 39.5); c.stroke();
      txt('KALMIX · APPLICATIONS', tx + 8, ty + 14, { fs: 8, c: 'rgba(232,239,240,.7)' });
      txt('SHEET ' + pad2(cur + 1) + '/' + pad2(G.length), tx + 8, ty + 33, { fs: 7.5 }); txt(flat ? 'SCALE 1:500' : 'VIEW OBLIQUE', tx + 99, ty + 33, { fs: 7.5 });
      txt('DATUM WGS 84', tx + 8, ty + 52, { fs: 7.5 }); txt('FIX · RTK', tx + 99, ty + 52, { fs: 7.5, c: 'rgba(61,220,132,.85)' });
    }

    /* ---------- 共用：线头、尾迹、定位点、切换、准线 ---------- */
    function head(x, y, ang) {
      var g = c.createRadialGradient(x, y, 0, x, y, 18); g.addColorStop(0, 'rgba(' + ICE + ',.55)'); g.addColorStop(1, 'rgba(' + ICE + ',0)');
      c.fillStyle = g; c.beginPath(); c.arc(x, y, 18, 0, 7); c.fill();
      var k = (t % 1.4) / 1.4; pt(x, y, 5 + 15 * k, { s: 'rgba(' + ICE + ',' + (.55 * (1 - k)) + ')', w: 1 }); pt(x, y, 3.4, { f: '#fff' });
      if (ang != null) { c.strokeStyle = 'rgba(255,255,255,.8)'; c.lineWidth = 1.3; c.beginPath(); c.moveTo(x + Math.cos(ang) * 6, y + Math.sin(ang) * 6); c.lineTo(x + Math.cos(ang) * 11, y + Math.sin(ang) * 11); c.stroke(); }
    }
    function trail(P, i0, i1, tail, base) {
      if (i1 <= i0) return; seg(P, i0, i1, { c: 'rgb(' + ICE + ')', w: 1.8, a: base == null ? .55 : base });
      var dh = P[i1].d, j = i1;
      while (j > i0 && dh - P[j].d < tail) { var j0 = Math.max(i0, j - 6), a = 1 - (dh - P[j].d) / tail; seg(P, j0, j, { c: 'rgb(' + ICE + ')', w: 1.8 + .6 * a, a: .55 + .45 * a }); j = j0; }
    }
    function dotsLayer(g, hd, lapLen, fl) {
      fl = fl || 70;
      for (var k = 0; k < g.dots.length; k++) {
        var p = g.dots[k], dd = hd == null ? -1 : (lapLen ? ((hd % lapLen) - p.d) : (hd - p.d));
        if (lapLen && hd != null && hd >= lapLen * 3 - 1) dd = 999;
        if (hd != null && dd >= 0) { if (dd < fl) { var a = 1 - dd / fl; pt(p.x, p.y, 1.6 + 5 * a, { f: 'rgba(190,232,255,' + (.55 * a) + ')' }); } pt(p.x, p.y, 1.5, { f: 'rgba(220,242,255,.9)' }); }
        else pt(p.x, p.y, 1.2, { f: 'rgba(' + ICE + ',.35)' });
      }
    }
    function morph(g, m) {
      for (var k = 0; k < N; k++) {
        var a = prev.dots[k], b = g.dots[k], qv = ease(cl((m - k / N * .35) / .65)), u = 1 - qv;
        var mx = (a.x + b.x) / 2 + (b.y - a.y) * .18, my = (a.y + b.y) / 2 - (b.x - a.x) * .18;
        pt(u * u * a.x + 2 * u * qv * mx + qv * qv * b.x, u * u * a.y + 2 * u * qv * my + qv * qv * b.y, 1.5, { f: 'rgba(190,232,255,' + (.45 + .45 * Math.sin(qv * Math.PI)) + ')' });
      }
    }
    function crosshair(g, hd) {
      if (!mouse) return;
      var P = g.P, lim = hd == null ? -1 : at(P, hd).i, best = null, bd = 1e9;
      for (var i = 0; i <= lim; i += 2) { var d = Math.hypot(P[i].x - mouse.x, P[i].y - mouse.y); if (d < bd) { bd = d; best = P[i]; } }
      c.strokeStyle = 'rgba(191,227,255,.16)'; c.lineWidth = 1 / s; c.setLineDash([2 / s, 4 / s]); c.beginPath();
      c.moveTo(mouse.x, (18 - oy) / s); c.lineTo(mouse.x, (ch - oy) / s); c.moveTo((18 - ox) / s, mouse.y); c.lineTo((cw - ox) / s, mouse.y); c.stroke(); c.setLineDash([]);
      if (best && bd < 60) {
        c.strokeStyle = 'rgba(' + ICE + ',.5)'; c.lineWidth = 1; c.beginPath(); c.moveTo(mouse.x, mouse.y); c.lineTo(best.x, best.y); c.stroke();
        pt(best.x, best.y, 6, { s: 'rgb(' + ICE + ')', w: 1.2 }); pt(best.x, best.y, 1.8, { f: '#fff' });
        var lx = best.x + 12, ly = best.y - 12; if (lx + 150 > LW) lx = best.x - 162;
        c.fillStyle = 'rgba(7,18,26,.86)'; c.strokeStyle = 'rgba(124,200,255,.25)'; rrect(lx, ly - 26, 150, 34, 4); c.fill(); c.stroke();
        txt('E ' + (412000 + best.x * .1).toFixed(2), lx + 8, ly - 13, { fs: 8.5, c: '#e8eff0' });
        txt('N ' + (3418000 + (LH - best.y) * .1).toFixed(2) + ' · FIXED', lx + 8, ly, { fs: 8.5, c: 'rgba(191,227,255,.75)' });
      }
    }

    /* ---------- 各场景 ---------- */
    function drawField(g, hd, e, lab) {
      g.cont.forEach(function (p) { poly(p, e, { c: 'rgba(143,163,171,.09)', close: 1 }); });
      poly(g.B.concat([g.B[0]]), e, { c: STR, w: 1.1 });
      poly(g.head, e, { c: ENV, dash: [3, 5] });
      var P = g.P, i;
      if (hd != null) { var iH = at(P, hd).i, i0 = -1; for (i = 0; i <= iH; i++) { var k = P[i].tag; if (k >= 0 && i0 < 0) i0 = i; if ((k < 0 || i === iH) && i0 >= 0) { seg(P, i0, i, { c: 'rgb(' + ICE + ')', w: g.swath, a: .07, cap: 'butt' }); i0 = -1; } } }
      dotsLayer(g, hd);
      if (hd != null) trail(P, 0, at(P, hd).i, 260);
      var offs = [[-10, -12, 'right'], [10, -12, 'left'], [12, 16, 'left'], [-12, 16, 'right']];
      g.ev.forEach(function (ev, i) {
        var p = ev.p, done = hd != null && hd >= ev.d, off = offs[i];
        c.save(); c.translate(p.x, p.y); c.rotate(Math.PI / 4); c.strokeStyle = STR; c.lineWidth = 1.2; c.fillStyle = done ? 'rgba(191,227,255,.9)' : '#0b1720'; c.beginPath(); c.rect(-4, -4, 8, 8); c.fill(); c.stroke(); c.restore();
        txt(ev.label, p.x + off[0], p.y + off[1], { a: off[2], q: lab, seed: i + 3, c: done ? '#e8eff0' : 'rgba(143,163,171,.85)' });
        if (done && hd - ev.d < 240) { var a = 1 - (hd - ev.d) / 240; pt(p.x, p.y, 6 + 18 * (1 - a), { s: 'rgba(190,232,255,' + a + ')', w: 1 }); txt('STORED', p.x + off[0], p.y + off[1] + 12, { a: off[2], fs: 8, c: 'rgba(61,220,132,' + a + ')' }); }
      });
      g.cp.forEach(function (cp, i) {
        var p = cp[0]; c.globalAlpha = e; c.strokeStyle = 'rgba(191,227,255,.7)'; c.lineWidth = 1.1; c.beginPath(); c.moveTo(p.x, p.y - 6); c.lineTo(p.x + 5.5, p.y + 4); c.lineTo(p.x - 5.5, p.y + 4); c.closePath(); c.stroke(); c.globalAlpha = 1;
        txt(cp[1], p.x + (i ? -10 : 10), p.y + 4, { a: i ? 'right' : 'left', q: lab, seed: 9 + i, c: '#bfe3ff' });
      });
      var X = g.X, A = X(30, 50), Bp = X(360, 50);
      txt('A', A.x - 4, A.y - 9, { q: lab, c: '#e8eff0', fs: 10, a: 'center' }); txt('B', Bp.x + 2, Bp.y - 9, { q: lab, c: '#e8eff0', fs: 10, a: 'center' });
      var d1 = X(420, 170), d2 = X(420, 210); c.globalAlpha = lab; c.strokeStyle = STR; c.lineWidth = 1; c.beginPath();
      c.moveTo(d1.x, d1.y); c.lineTo(d2.x, d2.y); c.moveTo(d1.x - 6, d1.y); c.lineTo(d1.x + 6, d1.y); c.moveTo(d2.x - 6, d2.y); c.lineTo(d2.x + 6, d2.y); c.stroke(); c.globalAlpha = 1;
      txt('SWATH 3.0 m', d1.x - 10, d1.y + 24, { a: 'right', q: lab, seed: 21 });
      var hl = X(36, 24); txt('HEADLAND', hl.x + 4, hl.y - 2, { q: lab, seed: 22, fs: 8 });
      if (hd != null) { var h = at(P, hd); head(h.x, h.y, h.ang); }
    }

    function drawRobot(g, hd, e, lab) {
      var P = g.P, tr = g.tree, a, k;
      c.globalAlpha = e; c.strokeStyle = STR; c.lineWidth = 1.1; rrect(64, 72, 500, 456, 14); c.stroke();
      c.strokeStyle = ENV; c.beginPath(); c.rect(452, 72, 112, 84); c.stroke();
      c.save(); c.beginPath(); c.rect(452, 72, 112, 84); c.clip(); c.beginPath(); for (k = -100; k < 200; k += 8) { c.moveTo(452 + k, 72); c.lineTo(536 + k, 156); } c.strokeStyle = 'rgba(143,163,171,.14)'; c.stroke(); c.restore();
      pt(tr.x, tr.y, 24, { s: ENV, f: 'rgba(143,163,171,.06)' });
      for (a = 0; a < 8; a++) { var r = a * Math.PI / 4; c.beginPath(); c.moveTo(tr.x + Math.cos(r) * 8, tr.y + Math.sin(r) * 8); c.lineTo(tr.x + Math.cos(r) * 20, tr.y + Math.sin(r) * 20); c.strokeStyle = 'rgba(143,163,171,.22)'; c.stroke(); }
      c.setLineDash([3, 5]); c.lineDashOffset = -t * 8; pt(tr.x, tr.y, g.Rk, { s: 'rgba(255,190,90,.5)', w: 1 }); c.setLineDash([]); c.lineDashOffset = 0; c.globalAlpha = 1;
      var docked = hd != null && hd >= P.L - 2, bl = docked ? .5 + .5 * Math.sin(t * 6) : 0;
      c.globalAlpha = e; c.strokeStyle = docked ? 'rgba(61,220,132,' + (.5 + .5 * bl) + ')' : STR; c.lineWidth = 1.2; rrect(70, 494, 42, 26, 4); c.stroke(); c.globalAlpha = 1;
      if (docked) { c.fillStyle = 'rgba(61,220,132,' + (.12 * bl + .05) + ')'; rrect(70, 494, 42, 26, 4); c.fill(); }
      txt('SHED', 508, 118, { a: 'center', q: lab, seed: 31, fs: 8.5 });
      txt('TREE · KEEP-OUT', tr.x + g.Rk + 10, tr.y + 3, { q: lab, seed: 32, fs: 8.5, c: 'rgba(255,200,120,.75)' });
      txt('DOCK', 122, 512, { q: lab, seed: 33, fs: 8.5, c: docked ? 'rgba(61,220,132,.95)' : 'rgba(143,163,171,.85)' });
      txt('LAWN · ZONE A', 80, 92, { q: lab, seed: 34, fs: 8.5 });
      if (hd != null) {
        var iH = at(P, hd).i, i0 = -1, k0 = -9;
        for (var i = 0; i <= iH; i++) { k = P[i].tag; if (k >= 0 && i0 < 0) { i0 = i; k0 = k; } if ((k !== k0 || i === iH) && i0 >= 0) { seg(P, i0, i, { c: 'rgb(' + ICE + ')', w: g.swath, a: k0 % 2 ? .05 : .095, cap: 'butt' }); i0 = -1; if (k >= 0) { i0 = i; k0 = k; } } }
      }
      dotsLayer(g, hd);
      if (hd != null) {
        trail(P, 0, at(P, hd).i, 300);
        var h = at(P, hd);
        c.save(); c.translate(h.x, h.y); c.rotate(h.ang);
        var gg = c.createRadialGradient(0, 0, 0, 0, 0, 20); gg.addColorStop(0, 'rgba(' + ICE + ',.5)'); gg.addColorStop(1, 'rgba(' + ICE + ',0)'); c.fillStyle = gg; c.beginPath(); c.arc(0, 0, 20, 0, 7); c.fill();
        c.fillStyle = '#e9f6ff'; rrect(-7, -5.5, 14, 11, 3); c.fill(); c.fillStyle = 'rgb(' + ICE + ')'; c.fillRect(4, -3.5, 2, 7); c.restore();
        var pk = (t % 1.4) / 1.4; pt(h.x, h.y, 8 + 14 * pk, { s: 'rgba(' + ICE + ',' + (.5 * (1 - pk)) + ')', w: 1 });
        if (!docked) { var below = h.y > tr.y && h.y < tr.y + 70, lft = h.x > LW - 120; txt('ROBOT · FIXED', h.x + (lft ? -14 : 14), h.y + (below ? 22 : -12), { fs: 8.5, c: '#3ddc84', a: lft ? 'right' : 'left' }); }
      }
    }

    function drawTrack(g, hd, e, lab) {
      var B = g.base, n = g.nrm, L = g.edgeL, R = g.edgeR, i;
      c.fillStyle = 'rgba(143,163,171,.035)'; c.beginPath(); L.forEach(function (p, j) { if (j) c.lineTo(p.x, p.y); else c.moveTo(p.x, p.y); }); for (i = R.length - 1; i >= 0; i--) c.lineTo(R[i].x, R[i].y); c.closePath(); c.globalAlpha = e; c.fill(); c.globalAlpha = 1;
      poly(L, e, { c: STR }); poly(R, e, { c: STR }); poly(B, e, { c: 'rgba(143,163,171,.18)', dash: [6, 8] });
      var lapLen = g.P.L / 3;
      function mk(d, label, sf) {
        var p = at(B, d), m = n(p.i); c.strokeStyle = sf ? 'rgba(232,239,240,.85)' : 'rgba(191,227,255,.4)'; c.lineWidth = sf ? 2 : 1; c.globalAlpha = e;
        c.beginPath(); c.moveTo(p.x + m.x * 19, p.y + m.y * 19); c.lineTo(p.x - m.x * 19, p.y - m.y * 19); c.stroke(); c.globalAlpha = 1;
        txt(label, p.x + m.x * 30, p.y + m.y * 30 + 3, { a: 'center', q: lab, seed: label.length + 40, fs: 8.5, c: sf ? '#e8eff0' : 'rgba(143,163,171,.85)' });
      }
      if (hd != null) { var ld = hd % lapLen; if (hd > 40 && ld < 60 && hd < g.P.L - 1) { var sp = at(B, 0); pt(sp.x, sp.y, 6 + 40 * (ld / 60), { s: 'rgba(232,239,240,' + (1 - ld / 60) + ')', w: 1.2 }); } }
      mk(0, 'S/F', 1); mk(B.L / 3, 'S1'); mk(B.L * 2 / 3, 'S2');
      var k = hd == null ? -1 : g.lap(hd);
      for (var j = 0; j < k; j++) { var lp0 = g.laps[j]; seg(lp0, 0, lp0.length - 1, { c: 'rgb(' + ICE + ')', w: 1.2, a: .18 + .12 * j }); }
      dotsLayer(g, hd, lapLen);
      if (hd != null) { var lp = g.laps[k], dl = Math.min(lp.L, hd - k * lapLen); trail(lp, 0, at(lp, dl).i, 280, .6); var h = at(lp, dl); head(h.x, h.y, h.ang); }
      var tx = 40, ty = 44; c.strokeStyle = 'rgba(191,227,255,.22)'; c.lineWidth = 1; c.strokeRect(tx + .5, ty + .5, 150, 70); c.beginPath(); c.moveTo(tx, ty + 18.5); c.lineTo(tx + 150, ty + 18.5); c.stroke();
      txt('LAP', tx + 8, ty + 13, { fs: 7.5 }); txt('TIME', tx + 60, ty + 13, { fs: 7.5 }); txt('Δ', tx + 142, ty + 13, { fs: 7.5, a: 'right' });
      for (j = 0; j < 3; j++) {
        var y = ty + 33 + j * 16, done = hd != null && (j < k || hd >= g.P.L - 1), cu = j === k && !done;
        txt(pad2(j + 1), tx + 8, y, { fs: 8.5, c: cu ? 'rgb(' + ICE + ')' : done ? '#e8eff0' : 'rgba(143,163,171,.5)' });
        if (done || cu) txt(done ? g.lapT[j] : '—:—', tx + 60, y, { fs: 8.5, c: cu ? 'rgb(' + ICE + ')' : '#e8eff0' });
        if (done && j) txt(g.lapD[j], tx + 142, y, { fs: 8.5, a: 'right', c: '#3ddc84' });
      }
    }

    var PAL = ['95,182,236', '140,205,245', '185,226,250', '225,243,255', '255,255,255'];
    var tgtW = V(6, 25);
    function drawApp(g, hd, e, lab) {
      var yaw = reduce ? .06 : Math.sin(t * .18) * .1 + .06 + (mouse ? (mouse.x / LW - .5) * .08 : 0);
      g.yaw = yaw;
      var i, q, a, P = g.P;
      // 地面透视网格
      c.lineWidth = 1 / s;
      for (var k = -30; k <= 30; k += 5) {
        var A1 = proj(k, -30, 0, yaw), B1 = proj(k, 30, 0, yaw), A2 = proj(-30, k, 0, yaw), B2 = proj(30, k, 0, yaw);
        c.strokeStyle = 'rgba(124,200,255,' + ((k % 15 ? .06 : .11) * e) + ')';
        c.beginPath(); c.moveTo(A1.x, A1.y); c.lineTo(B1.x, B1.y); c.moveTo(A2.x, A2.y); c.lineTo(B2.x, B2.y); c.stroke();
      }
      // 点云：扫到之前极淡；扫到时抖动重影，随后收拢配准
      var h = hd != null ? at(P, hd) : null, sweep = t * 2.4;
      var settle = reduce ? 1 : cl((t - MORPH - g.solid) / 1.2); // 线头停下后，末段扫到的点也收拢到位
      c.globalCompositeOperation = 'lighter';
      for (i = 0; i < g.cloud.length; i++) {
        q = g.cloud[i];
        var rev = h && q.rd >= 0 && hd >= q.rd, al, sz, col = q.k ? Math.min(4, (q.e ? 2 : 1) + Math.floor(q.z / 3.4)) : 0;
        if (!rev) { if (q.k || !(i % 2)) { var o = proj(q.x, q.y, q.z, yaw); c.globalAlpha = (q.k ? .08 : .035) * e; c.fillStyle = 'rgb(' + PAL[col] + ')'; c.fillRect(o.x - .6, o.y - .6, 1.2, 1.2); } continue; }
        a = Math.max(cl((hd - q.rd) / 6), settle); var jj = (1 - a) * (1 - a);
        var pp = proj(q.x + q.jx * jj, q.y + q.jy * jj, q.z + q.jz * jj, yaw);
        al = (q.e ? 1 : q.k ? .5 : .42) * cl(1.45 - pp.d / 120);
        var ang = Math.atan2(q.y - h.y, q.x - h.x), dA = Math.abs(((ang - sweep) % 6.283 + 9.425) % 6.283 - 3.1416);
        if (dA < .35 && Math.hypot(q.x - h.x, q.y - h.y) < 16) { al = Math.min(1, al + .5 * (1 - dA / .35)); col = 4; }
        if (jj > .05) { c.globalAlpha = al * .5 * jj; c.fillStyle = 'rgb(' + PAL[col] + ')'; var gp = proj(q.x - q.jx * jj, q.y - q.jy * jj, q.z, yaw); c.fillRect(gp.x - .7, gp.y - .7, 1.4, 1.4); }
        sz = Math.max(1, pp.k * (q.e ? .17 : .15));
        c.globalAlpha = al; c.fillStyle = 'rgb(' + PAL[col] + ')'; c.fillRect(pp.x - sz / 2, pp.y - sz / 2, sz, sz);
      }
      c.globalAlpha = 1; c.globalCompositeOperation = 'source-over';
      // 待走路径上的定位点 + 已走轨迹（投影到屏幕后沿用平面画法）
      var SP = P.map(function (w) { var o = proj(w.x, w.y, 0, yaw); return { x: o.x, y: o.y, d: w.d }; }); SP.L = P.L;
      g.dots = g.wdots.map(function (w) { var o = proj(w.x, w.y, 0, yaw); return { x: o.x, y: o.y, d: w.d }; });
      dotsLayer(g, hd, 0, 5);
      var f0 = SP[0]; pt(f0.x, f0.y, 5, { f: '#3ddc84' }); txt('FIX', f0.x + 12, f0.y + 16, { q: lab, seed: 51, c: '#3ddc84' });
      if (h) {
        trail(SP, 0, at(P, hd).i, 18, .75);
        // 扫描圈与扫描线
        var ring = []; for (i = 0; i <= 64; i++) { var th = i / 64 * 6.283, rp = proj(h.x + 16 * Math.cos(th), h.y + 16 * Math.sin(th), 0, yaw); ring.push(rp); }
        c.setLineDash([2, 5]); c.lineDashOffset = -t * 10; poly(ring, 1, { c: 'rgba(' + ICE + ',.35)', w: 1 }); c.setLineDash([]);
        var se = proj(h.x + 16 * Math.cos(sweep), h.y + 16 * Math.sin(sweep), 0, yaw), hp = proj(h.x, h.y, 0, yaw);
        var lg = c.createLinearGradient(hp.x, hp.y, se.x, se.y); lg.addColorStop(0, 'rgba(' + ICE + ',.6)'); lg.addColorStop(1, 'rgba(' + ICE + ',0)');
        c.strokeStyle = lg; c.lineWidth = 1.2; c.beginPath(); c.moveTo(hp.x, hp.y); c.lineTo(se.x, se.y); c.stroke();
      }
      // 终点：鼠标在地面上放置（无鼠标时缓慢漂移）
      var want = mouse ? unproj(mouse.x, mouse.y, yaw) : V(6 + 3.5 * Math.sin(t * .5), 25 + 2.5 * Math.sin(t * .8));
      tgtW = reduce ? want : V(tgtW.x + (want.x - tgtW.x) * .08, tgtW.y + (want.y - tgtW.y) * .08);
      if (hd != null && hd >= P.L - .01) {
        var E = P[P.length - 1], Ep = P[P.length - 8], dx = E.x - Ep.x, dy = E.y - Ep.y, l = Math.hypot(dx, dy) || 1, kk = Math.hypot(tgtW.x - E.x, tgtW.y - E.y) * .45;
        var CX = E.x + dx / l * kk, CY = E.y + dy / l * kk, grow = reduce ? 1 : cl((t - MORPH - g.solid) / .8), dash = [];
        for (i = 0; i <= 30; i++) { var u = i / 30 * grow, w1 = 1 - u; dash.push(proj(w1 * w1 * E.x + 2 * w1 * u * CX + u * u * tgtW.x, w1 * w1 * E.y + 2 * w1 * u * CY + u * u * tgtW.y, 0, yaw)); }
        c.setLineDash([4, 7]); c.lineDashOffset = -t * 18; poly(dash, 1, { c: 'rgba(' + ICE + ',.9)', w: 1.6 }); c.setLineDash([]); c.lineDashOffset = 0;
        if (grow > 0) {
          var T0 = proj(tgtW.x, tgtW.y, 0, yaw), T1 = proj(tgtW.x, tgtW.y, 7, yaw), rg = [], rg2 = [];
          for (i = 0; i <= 48; i++) { var tt2 = i / 48 * 6.283 + t * .6; rg.push(proj(tgtW.x + 1.8 * Math.cos(tt2), tgtW.y + 1.8 * Math.sin(tt2), 0, yaw)); rg2.push(proj(tgtW.x + (3.4 + .3 * Math.sin(t * 2)) * Math.cos(tt2), tgtW.y + (3.4 + .3 * Math.sin(t * 2)) * Math.sin(tt2), 0, yaw)); }
          c.setLineDash([3, 4]); poly(rg, 1, { c: 'rgba(' + ICE + ',' + grow + ')', w: 1.4 }); c.setLineDash([]); poly(rg2, 1, { c: 'rgba(124,200,255,' + (.25 * grow) + ')' });
          c.strokeStyle = 'rgba(191,227,255,' + (.45 * grow) + ')'; c.lineWidth = 1; c.beginPath(); c.moveTo(T0.x, T0.y); c.lineTo(T1.x, T1.y); c.stroke();
          pt(T1.x, T1.y, 2, { f: 'rgba(232,239,240,' + grow + ')' });
          var right = T1.x > 200, lx = T1.x + (right ? -10 : 10), al2 = right ? 'right' : 'left';
          txt('YOUR NEXT POINT', lx, T1.y - 4, { fs: 10.5, c: 'rgba(232,239,240,' + grow + ')', a: al2 });
          txt('E ' + (412000 + tgtW.x * 10).toFixed(2) + ' · N ' + (3418000 + tgtW.y * 10).toFixed(2), lx, T1.y + 12, { fs: 8, a: al2, c: 'rgba(143,163,171,' + (.9 * grow) + ')' });
        }
        var hE = SP[SP.length - 1]; head(hE.x, hE.y, null);
        if (fine) txt(mouse ? 'CLICK TO BUILD WITH KALMIX' : 'MOVE TO PLACE YOUR NEXT POINT', LW / 2, LH - 30, { a: 'center', fs: 8.5, c: 'rgba(191,227,255,.7)' });
      } else if (h) { var hh = at(SP, hd); head(hh.x, hh.y, hh.ang); }
    }
    var DRAW = { field: drawField, robot: drawRobot, track: drawTrack, app: drawApp };

    /* ---------- 一帧 ---------- */
    function draw() {
      if (!cw) return;
      var g = G[cur], tt = Math.min(t, g.D);
      c.setTransform(DPR, 0, 0, DPR, 0, 0); c.clearRect(0, 0, cw, ch);
      chrome();
      c.setTransform(DPR * s, 0, 0, DPR * s, DPR * ox, DPR * oy);
      var e = cl((tt - .25) / 1.1), lab = cl((tt - .6) / .9), hd = null;
      if (tt >= MORPH) hd = g.kind === 'app' ? Math.min(g.P.L, (tt - MORPH) / g.solid * g.P.L) : Math.min(g.P.L, (tt - MORPH) / (g.D - MORPH - 1.4) * g.P.L);
      DRAW[g.kind](g, hd, e, lab);
      if (tt < MORPH + .15) morph(g, cl(tt / MORPH));
      if (g.kind !== 'app') crosshair(g, hd);
      c.setTransform(1, 0, 0, 1, 0, 0);
      var st = hd == null ? 'REPLANNING PATH' : g.state(hd);
      if (st !== draw._st) { rx2.textContent = st; draw._st = st; rx2.classList.toggle('is-fixed', /RTK FIXED/.test(st)); }
      cards[cur].style.setProperty('--p', cl(tt / g.D).toFixed(4));
      if (KMX.progress) KMX.progress(sec, cl(tt / g.D));
    }

    /* ---------- 切换 ---------- */
    var rx1 = q('rx1'), rx2 = q('rx2'), barT = q('bar'), barN = q('note'), live = q('live'), legend = q('legend');
    function go(i, fromAuto) {
      if (i !== cur || !started) { prev = started ? G[cur] : scatter; }
      cur = i; t = reduce ? G[i].D : 0;
      cards.forEach(function (el, k) { var on = k === i; el.classList.toggle('is-on', on); var b = el.querySelector('.kmx-app__hd'); if (b) b.setAttribute('aria-expanded', on ? 'true' : 'false'); });
      var el = cards[i];
      rx1.textContent = 'PATH ' + pad2(i + 1) + '/' + pad2(G.length) + ' · ' + G[i].title.toUpperCase();
      barT.textContent = el.getAttribute('data-bar') || G[i].title; var nt = el.getAttribute('data-note') || ''; barN.textContent = nt ? ' · ' + nt : '';
      live.textContent = G[i].kind === 'app' ? 'OPEN' : 'DEMO';
      cv.style.cursor = G[i].kind === 'app' ? 'pointer' : 'crosshair';
      legend.innerHTML = G[i].legend;
      placeDots(); draw();
    }

    /* ---------- 知识点：按逻辑坐标放到图纸上 ---------- */
    function placeDots() {
      var br = board.getBoundingClientRect(), sr = stage.getBoundingClientRect(), vr = cv.getBoundingClientRect();
      kdots.forEach(function (k) {
        var on = k.getAttribute('data-kind') === G[cur].kind;
        k.classList.toggle('is-on', on);
        var x = parseFloat(k.getAttribute('data-x')) / 100 * LW, y = parseFloat(k.getAttribute('data-y')) / 100 * LH;
        k.style.left = (vr.left - sr.left + ox + x * s) + 'px'; k.style.top = (vr.top - sr.top + oy + y * s) + 'px';
        k.setAttribute('tabindex', on ? '0' : '-1');
      });
      void br;
    }
    kdots.forEach(function (k) {
      function place() { var tip = k.querySelector('.kmx-kdot__tip'); if (!tip) return; k.classList.remove('is-flip'); if (k.getBoundingClientRect().right + tip.getBoundingClientRect().width + 24 > window.innerWidth) k.classList.add('is-flip'); }
      k.addEventListener('mouseenter', place); k.addEventListener('focus', place);
    });

    /* ---------- 交互 ---------- */
    var stackEl = sec.querySelector('.kmx-app__stack');
    cards.forEach(function (el, i) {
      el.addEventListener('pointermove', function (ev) { var r = el.getBoundingClientRect(); el.style.setProperty('--mx', (ev.clientX - r.left) + 'px'); el.style.setProperty('--my', (ev.clientY - r.top) + 'px'); });
      el.addEventListener('pointerenter', function () { el.classList.add('is-hov'); });
      el.addEventListener('pointerleave', function () { el.classList.remove('is-hov'); });
      var b = el.querySelector('.kmx-app__hd');
      if (b) b.addEventListener('click', function () { auto = false; sec.classList.add('is-held'); if (i !== cur) go(i); });
    });
    if (stackEl) { stackEl.addEventListener('pointerenter', function () { hold = true; }); stackEl.addEventListener('pointerleave', function () { hold = false; }); }
    function toLogical(ev) { var r = cv.getBoundingClientRect(); return V((ev.clientX - r.left - ox) / s, (ev.clientY - r.top - oy) / s); }
    cv.addEventListener('pointermove', function (ev) { mouse = toLogical(ev); if (!raf) draw(); });
    cv.addEventListener('pointerdown', function (ev) { mouse = toLogical(ev); if (!raf) draw(); });
    cv.addEventListener('click', function () {
      if (G[cur].kind !== 'app') return;
      if (!fine && mouse) { var T = proj(tgtW.x, tgtW.y, 0, G[cur].yaw); if (Math.hypot(T.x - mouse.x, T.y - mouse.y) > 40) return; } // 触屏：先点一下放置终点，再点终点进入
      var go2 = cards[cur].querySelector('.kmx-app__go'); if (go2 && go2.href) window.location.href = go2.href;
    });
    cv.addEventListener('pointerleave', function () { mouse = null; if (!raf) draw(); });

    /* ---------- 动画循环：只在可见时运行 ---------- */
    function frame(now) {
      raf = 0; if (!visible) return;
      var dt = last ? Math.min(.05, (now - last) / 1000) : 0; last = now;
      t += dt;
      if (t > G[cur].D + .6 && auto && !hold) go((cur + 1) % G.length, true);
      draw();
      raf = requestAnimationFrame(frame);
    }
    function run(v) {
      visible = v;
      if (v && !reduce && !raf) { last = 0; raf = requestAnimationFrame(frame); }
      if (!v && raf) { cancelAnimationFrame(raf); raf = 0; }
      if (!v && !reduce && !auto) { auto = true; sec.classList.remove('is-held'); } // 离开后恢复自动轮播
    }

    if (window.ResizeObserver) new ResizeObserver(size).observe(cv); else window.addEventListener('resize', size);
    size();
    go(0); started = true;
    if (reduce) { draw(); }
    KMX.onEnter(sec, function () { if (!reduce) { prev = scatter; t = 0; } });
    KMX.onVisible(sec, run);
    if (!window.IntersectionObserver) run(true);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { draw(); });
  }

  function boot() { document.querySelectorAll('.kmx-app').forEach(initApply); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', function (e) { var s = e.target.querySelector('.kmx-app'); if (s) initApply(s); });
})();
